function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function median(sortedValues: readonly number[]): number {
  const middleIndex = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 0) {
    return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
  }

  return sortedValues[middleIndex];
}

function filterOutliers(values: readonly number[]): number[] {
  if (values.length < 3) {
    return [...values];
  }

  const sortedValues = [...values].sort((left, right) => left - right);
  const center = median(sortedValues);
  const deviations = sortedValues.map((value) => Math.abs(value - center));
  const sortedDeviations = [...deviations].sort((left, right) => left - right);
  const medianAbsoluteDeviation = median(sortedDeviations);

  if (medianAbsoluteDeviation === 0) {
    return sortedValues.filter((value) => value === center);
  }

  return sortedValues.filter((value) => (0.6745 * Math.abs(value - center)) / medianAbsoluteDeviation <= 3.5);
}

export function computeRobustAverage(values: readonly (number | null | undefined)[]): number | undefined {
  const numericValues = values.filter(isFiniteNumber);
  if (numericValues.length === 0) {
    return undefined;
  }

  const filteredValues = filterOutliers(numericValues);
  return mean(filteredValues.length > 0 ? filteredValues : numericValues);
}

export function computePriceRange(values: readonly (number | null | undefined)[]): { minPrice?: number; maxPrice?: number } {
  const numericValues = values.filter(isFiniteNumber);

  if (numericValues.length === 0) {
    return {};
  }

  return {
    minPrice: Math.min(...numericValues),
    maxPrice: Math.max(...numericValues),
  };
}
